import format from 'date-fns/format';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Icon, Stack, Text } from '@eluve/components';
import { useIsEluveAdmin } from '@eluve/session-helpers';

import { CitationModal } from './CitationModal';
import { Citation } from './CitationText';
import { getSourceTypeLabel, getSourceUrl } from './citation';
import { getSourceIcon } from './citation';

const MAX_VISIBLE_CITATIONS = 20;
const DEFAULT_VISIBLE_COUNT = 6;

interface CitationsSectionProps {
  citations: Citation[];
  maxVisibleCitations?: number;
  defaultVisibleCount?: number;
  onSourceClick?: (citation: Citation) => void;
}

const formatSourceDate = (timestamp?: string) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    return format(date, 'MM/dd/yyyy');
  } catch {
    return timestamp;
  }
};

export const CitationsSection = ({
  citations,
  maxVisibleCitations = MAX_VISIBLE_CITATIONS,
  defaultVisibleCount = DEFAULT_VISIBLE_COUNT,
  onSourceClick,
}: CitationsSectionProps) => {
  const isEluveAdmin = useIsEluveAdmin();
  const { tenantId, patientId } = useParams<{
    tenantId: string;
    patientId: string;
  }>();

  const [showAll, setShowAll] = useState(false);
  const [clickedCitation, setClickedCitation] = useState<Citation | null>(null);

  // Create unique citations by source identity, maintaining citation numbers
  const getUniqueCitationsWithNumbers = (citations: Citation[]) => {
    const sourceMap = new Map<string, { citation: Citation; number: number }>();
    let citationNumber = 1;

    citations.forEach((citation) => {
      const sourceKey = `${citation.source.sourceType}-${citation.source.sourceDocId}`;
      if (!sourceMap.has(sourceKey)) {
        sourceMap.set(sourceKey, { citation, number: citationNumber++ });
      }
    });

    return Array.from(sourceMap.values());
  };

  // Get unique citations with their assigned numbers
  const uniqueCitationsWithNumbers = getUniqueCitationsWithNumbers(citations);

  // Sort unique citations by citation number (ascending order)
  const sortedUniqueCitations = uniqueCitationsWithNumbers.sort((a, b) => {
    return a.number - b.number;
  });

  const visibleCitations = showAll
    ? sortedUniqueCitations.slice(0, maxVisibleCitations)
    : sortedUniqueCitations.slice(0, defaultVisibleCount);

  const hasMoreCitations = sortedUniqueCitations.length > defaultVisibleCount;

  const handleCitationClick = (citation: Citation) => {
    onSourceClick?.(citation);
    if (isEluveAdmin) {
      // Open citation modal with JSON details for admin users to better inspection
      setClickedCitation(citation);
      return;
    }
    // For non-admin users, open the source URL directly
    const sourceUrl = getSourceUrl(citation, tenantId, patientId);
    if (sourceUrl) {
      window.open(sourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div>
      <Stack gap={2} fullWidth={false}>
        <Stack gap={3} className="flex-1">
          <Text color="secondary">Sources:</Text>
          <Stack
            justify="start"
            align="start"
            gap={2}
            direction="horizontal"
            wrap
          >
            {visibleCitations.map((citationWithNumber) => {
              const { citation, number: citationNumber } = citationWithNumber;
              const sourceLabel = getSourceTypeLabel(
                citation.source.sourceType,
              );
              const displayText = citation.source.sourceTimestamp
                ? `[${citationNumber}] ${sourceLabel} - ${formatSourceDate(citation.source.sourceTimestamp)}`
                : `[${citationNumber}] ${sourceLabel}`;

              return (
                <Button
                  variant="secondary"
                  size="sm"
                  key={`${citation.source.sourceDocId}-${citationNumber}`}
                  onClick={() => handleCitationClick(citation)}
                >
                  <Icon
                    name={getSourceIcon(citation.source.sourceType)}
                    size="auto"
                  />
                  {displayText}
                </Button>
              );
            })}
          </Stack>
        </Stack>
        {hasMoreCitations && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll
              ? 'View Less'
              : `View All (${Math.min(maxVisibleCitations, sortedUniqueCitations.length)})`}
          </Button>
        )}
      </Stack>

      {clickedCitation && (
        <CitationModal
          citation={clickedCitation}
          isOpen={true}
          onClose={() => setClickedCitation(null)}
        />
      )}
    </div>
  );
};
